/**
 * Pantalla de control del AUTO-RECOBRO (idea de Yago, 2026-08-08).
 *
 * El default del sistema es ON para todo el mundo: el motor reintenta el cobro
 * al método ya autorizado antes de cada mensaje (R6) y, si cobra, el mensaje
 * nunca sale. Esta pantalla lista las EXCEPCIONES — los clientes que Ops apagó
 * (supresiones `kind='auto_retry'`) — con su motivo, quién y cuándo, y permite
 * reactivarlos. Independiente del Pausado clásico: aquí no se silencian
 * mensajes, solo cobros automáticos.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { deleteSuppression, listSuppressions } from "../../api/msops";
import type { Suppression } from "../../types/msops";
import RecobrosTabs from "./components/RecobrosTabs";
import { Card, COLORS as UI, ErrorBanner, Loading, Td, Th } from "./components/ui";

// Paleta del kit compartido, como el resto de la sección (ver Cartera.tsx).
const COLORS = {
  heading: UI.heading,
  muted: UI.muted,
  border: UI.border,
  link: UI.link,
};

export default function AutoRecobro() {
  const { token } = useAuth();
  const [rows, setRows] = useState<Suppression[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listSuppressions(
        { kind: "auto_retry", active: true, pageSize: 100 },
        { token },
      );
      setRows(res.items);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const reactivate = useCallback(
    async (id: string) => {
      setBusy(id);
      try {
        await deleteSuppression(id, { token });
        await load();
      } catch (err) {
        window.alert(`No se pudo reactivar: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setBusy(null);
      }
    },
    [token, load],
  );

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <RecobrosTabs />
      <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>
        Reintento automático de cobro — excepciones
      </h2>
      <p
        style={{
          margin: "0.25rem 0 1rem",
          color: COLORS.muted,
          fontSize: ".85rem",
          maxWidth: 760,
        }}
      >
        El auto-recobro está <strong>ON por defecto para todos</strong>: el motor reintenta el
        cobro al método ya autorizado antes de cada mensaje, y si cobra, el mensaje no sale.
        Aquí están los clientes que Ops apagó — sus <em>mensajes</em> siguen su calendario.
        Se apaga desde la propia tarea o desde el caso.
      </p>

      {error && <ErrorBanner message={error} />}
      {rows === null && !error && <Loading label="Cargando excepciones…" />}
      {rows !== null && rows.length === 0 && (
        <div style={{ color: COLORS.muted, fontSize: ".85rem" }}>
          Sin excepciones: el auto-recobro está activo para todos los clientes.
        </div>
      )}
      {rows !== null && rows.length > 0 && (
        <Card style={{ padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: ".85rem", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Cliente</Th>
              <Th>Motivo</Th>
              <Th>Quién / cuándo</Th>
              <Th>Hasta</Th>
              <Th> </Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((sup) => (
              <tr key={sup.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: "0.45rem 0.5rem" }}>
                  {sup.leadUlid ? (
                    <Link to={`/leads/${sup.leadUlid}`} target="_blank" rel="noreferrer" style={{ color: COLORS.link }}>
                      {sup.leadUlid}
                    </Link>
                  ) : (
                    (sup.email ?? "—")
                  )}
                </td>
                <td style={{ padding: "0.45rem 0.5rem" }}>{sup.reason}</td>
                <td style={{ padding: "0.45rem 0.5rem", color: COLORS.muted }}>
                  {sup.createdBy} · {new Date(sup.createdAt).toLocaleString("es-ES")}
                </td>
                <td style={{ padding: "0.45rem 0.5rem", color: COLORS.muted }}>
                  {sup.until ? new Date(sup.until).toLocaleDateString("es-ES") : "indefinido"}
                </td>
                <td style={{ padding: "0.45rem 0.5rem" }}>
                  <button
                    type="button"
                    disabled={busy === sup.id}
                    onClick={() => reactivate(sup.id)}
                    style={{
                      background: "none",
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: "0.35rem",
                      padding: "0.15rem 0.55rem",
                      cursor: "pointer",
                      color: COLORS.link,
                    }}
                  >
                    {busy === sup.id ? "…" : "Reactivar"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </Card>
      )}
    </div>
  );
}
